import { useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { jiraApi, type JiraConfig } from '@/services/api';
import type { LabsFeature } from '@/services/api';

// Display-friendly labels for modules that have no toggleable "anchor" row of their own
// (moduleKey === featureKey) to source a label from — falls back to the raw moduleKey otherwise.
const MODULE_LABEL_OVERRIDES: Record<string, string> = {
  views: 'Views',
  integrations: 'Integrations',
};

interface LabsTabProps {
  labsFeatures: LabsFeature[];
  labsSaving: boolean;
  labsSaved: boolean;
  labsSaveError: string;
  onToggle: (featureKey: string, isEnabled: boolean) => void;
  onSave: () => void;
}

export function LabsTab({ labsFeatures, labsSaving, labsSaved, labsSaveError, onToggle, onSave }: LabsTabProps) {
  const fc = useFieldConfig();
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  // Jira config form state
  const jiraFeature = labsFeatures.find((f) => f.featureKey === 'jira');
  const jiraEnabled = jiraFeature?.isEnabled ?? false;
  const [jiraConfig, setJiraConfig] = useState<JiraConfig>({ instanceUrl: null, email: null, apiToken: null, updatedAt: null });
  const [jiraForm, setJiraForm] = useState({ instanceUrl: '', email: '', apiToken: '' });
  const [jiraSaving, setJiraSaving] = useState(false);
  const [jiraSaved, setJiraSaved] = useState(false);
  const [jiraSaveError, setJiraSaveError] = useState('');
  const [jiraTesting, setJiraTesting] = useState(false);
  const [jiraTestResult, setJiraTestResult] = useState<{ ok: boolean; displayName?: string; error?: string } | null>(null);

  useEffect(() => {
    if (!jiraEnabled) return;
    jiraApi.getConfig().then((cfg) => {
      setJiraConfig(cfg);
      setJiraForm({
        instanceUrl: cfg.instanceUrl || '',
        email: cfg.email || '',
        apiToken: cfg.apiToken || '',
      });
    }).catch(() => {});
  }, [jiraEnabled]);

  const handleJiraSave = async () => {
    setJiraSaving(true);
    setJiraSaved(false);
    setJiraSaveError('');
    try {
      const updated = await jiraApi.saveConfig({
        instanceUrl: jiraForm.instanceUrl,
        email: jiraForm.email,
        apiToken: jiraForm.apiToken || undefined,
      });
      setJiraConfig(updated);
      setJiraForm((f) => ({ ...f, apiToken: updated.apiToken || '' }));
      setJiraSaved(true);
      setTimeout(() => setJiraSaved(false), 3000);
      // Also persist the Labs toggle state so Jira stays enabled after navigation
      onSave();
    } catch (e: any) {
      setJiraSaveError(e.message || 'Failed to save Jira settings');
    } finally {
      setJiraSaving(false);
    }
  };

  const handleJiraTest = async () => {
    setJiraTesting(true);
    setJiraTestResult(null);
    try {
      const result = await jiraApi.testConnection();
      setJiraTestResult(result);
    } catch (e: any) {
      setJiraTestResult({ ok: false, error: e.message });
    } finally {
      setJiraTesting(false);
    }
  };

  const toggleExpand = (moduleKey: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      next.has(moduleKey) ? next.delete(moduleKey) : next.add(moduleKey);
      return next;
    });
  };

  // Collect unique moduleKeys in display order
  const moduleKeys = Array.from(
    new Map(labsFeatures.map((f) => [f.moduleKey, f.displayOrder])).entries()
  )
    .sort((a, b) => a[1] - b[1])
    .map(([key]) => key);

  return (
    <div className="max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-1.5">
          <h2 className="text-base font-semibold text-text-primary">Labs</h2>
          <div className="relative group">
            <button type="button" className="text-text-tertiary hover:text-text-secondary flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
              </svg>
            </button>
            <div className="absolute left-0 top-6 z-50 w-64 bg-gray-900 text-white text-xs rounded-md px-3 py-2 invisible group-hover:visible pointer-events-none shadow-lg">
              Enable or disable optional features. Changes apply immediately for all users in your organisation.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-6">
          {labsSaved && <span className="text-sm text-green-600">✓ Saved</span>}
          {labsSaveError && <span className="text-sm text-red-600">{labsSaveError}</span>}
          <button
            onClick={onSave}
            disabled={labsSaving}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {labsSaving ? 'Saving...' : 'Save Labs Settings'}
          </button>
        </div>
      </div>

      {labsFeatures.length === 0 ? (
        <p className="text-sm text-text-secondary">No labs features configured.</p>
      ) : (
        moduleKeys.map((moduleKey) => {
          // A "Core Module" entry is one where featureKey === moduleKey
          const moduleEntry = labsFeatures.find((f) => f.featureKey === moduleKey);
          const isToggleableModule = !!moduleEntry;
          const moduleEnabled = moduleEntry ? moduleEntry.isEnabled : true;
          const isExpanded = expandedModules.has(moduleKey);

          // Features: direct children of the module entry (or top-level non-module features)
          const features = labsFeatures
            .filter((f) => {
              if (isToggleableModule) {
                // Children of the module entry
                return f.parentFeatureKey === moduleKey;
              } else {
                // Top-level features in this module (no parent)
                return f.moduleKey === moduleKey && !f.parentFeatureKey;
              }
            })
            .sort((a, b) => a.displayOrder - b.displayOrder);

          const moduleLabel = moduleKey === 'Initiatives'
            ? fc.label('initiative')
            : (moduleEntry?.label ?? MODULE_LABEL_OVERRIDES[moduleKey] ?? moduleKey);

          return (
            <div key={moduleKey} className="border border-border-default rounded-md overflow-hidden">
              {/* Module header */}
              {isToggleableModule ? (
                // Toggleable Core Module — accordion header with toggle
                <div
                  className="bg-amber-50/70 px-4 py-3 flex items-center justify-between border-b border-border-default cursor-pointer select-none"
                  onClick={() => toggleExpand(moduleKey)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <ChevronDownIcon
                      className={`w-4 h-4 text-text-secondary flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-text-primary">{moduleLabel}</span>
                      <span className="ml-2 text-xs text-amber-700 font-medium bg-amber-100 px-2 py-0.5 rounded-full">Core Module</span>
                      {moduleEntry?.description && (
                        <p className="text-xs text-text-secondary mt-0.5">{moduleEntry.description}</p>
                      )}
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <ToggleSwitch
                      enabled={moduleEnabled}
                      onChange={(val) => onToggle(moduleKey, val)}
                    />
                  </div>
                </div>
              ) : (
                // Always-on module header — accordion, no toggle
                <div
                  className="bg-blue-50/70 px-4 py-3 flex items-center justify-between border-b border-border-default cursor-pointer select-none"
                  onClick={() => toggleExpand(moduleKey)}
                >
                  <div className="flex items-center gap-2">
                    <ChevronDownIcon
                      className={`w-4 h-4 text-text-secondary flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                    <span className="text-sm font-semibold text-text-primary">{moduleLabel}</span>
                    <span className="text-xs text-text-tertiary">Core Module</span>
                  </div>
                  <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">Always on</span>
                </div>
              )}

              {/* Feature rows — collapsed by default for all modules */}
              {isExpanded && (
                <div className={`divide-y divide-border-default ${!moduleEnabled && isToggleableModule ? 'opacity-40 pointer-events-none' : ''}`}>
                  {features.map((feature) => {
                    const subFeatures = labsFeatures
                      .filter((f) => f.parentFeatureKey === feature.featureKey)
                      .sort((a, b) => a.displayOrder - b.displayOrder);

                    return (
                      <div key={feature.featureKey} className="last:border-b-0">
                        {/* Feature row */}
                        <div className="px-4 py-3 flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-text-primary">{feature.label}</p>
                              {['intelligenceHealth', 'intelligenceAsk', 'intelligenceDigest'].includes(feature.featureKey) && (
                                <span className="text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">alpha</span>
                              )}
                            </div>
                            {feature.description && (
                              <p className="text-xs text-text-secondary mt-0.5">{feature.description}</p>
                            )}
                          </div>
                          <ToggleSwitch
                            enabled={feature.isEnabled}
                            onChange={(val) => onToggle(feature.featureKey, val)}
                          />
                        </div>

                        {/* Sub-feature rows */}
                        {subFeatures.map((sub) => (
                          <div
                            key={sub.featureKey}
                            className="px-4 py-2 bg-gray-50/60 flex items-center gap-2 border-t border-gray-100"
                          >
                            <span className="text-text-tertiary text-xs flex-shrink-0">↳</span>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-medium text-text-secondary">{sub.label}</span>
                              {sub.description && (
                                <span className="text-xs text-text-tertiary ml-2">{sub.description}</span>
                              )}
                            </div>
                            {feature.isEnabled && (
                              <span className="text-xs text-green-600 font-medium flex-shrink-0">Auto-enabled ✓</span>
                            )}
                          </div>
                        ))}

                        {/* Jira inline config form — shown when jira feature is enabled */}
                        {feature.featureKey === 'jira' && feature.isEnabled && (
                          <div className="px-4 py-4 bg-blue-50/40 border-t border-blue-100 space-y-3">
                            <p className="text-xs font-semibold text-text-primary">Jira Cloud Connection</p>
                            <div>
                              <label className="block text-xs font-medium text-text-secondary mb-1">Instance URL</label>
                              <input
                                type="text"
                                value={jiraForm.instanceUrl}
                                onChange={(e) => setJiraForm((f) => ({ ...f, instanceUrl: e.target.value }))}
                                placeholder="https://yourcompany.atlassian.net"
                                className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-white text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-text-secondary mb-1">Service Account Email</label>
                              <input
                                type="email"
                                value={jiraForm.email}
                                onChange={(e) => setJiraForm((f) => ({ ...f, email: e.target.value }))}
                                placeholder="serviceaccount@yourcompany.com"
                                className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-white text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-text-secondary mb-1">
                                API Token{jiraConfig.apiToken ? ' (leave blank to keep existing)' : ''}
                              </label>
                              <input
                                type="password"
                                value={jiraForm.apiToken}
                                onChange={(e) => setJiraForm((f) => ({ ...f, apiToken: e.target.value }))}
                                placeholder={jiraConfig.apiToken ? '••••••••' : 'Paste your Jira API token'}
                                className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-white text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            {jiraTestResult && (
                              <p className={`text-xs ${jiraTestResult.ok ? 'text-green-600' : 'text-red-600'}`}>
                                {jiraTestResult.ok
                                  ? `✓ Connected as ${jiraTestResult.displayName}`
                                  : `✗ ${jiraTestResult.error}`}
                              </p>
                            )}
                            {jiraSaveError && <p className="text-xs text-red-600">{jiraSaveError}</p>}
                            {jiraSaved && <p className="text-xs text-green-600">✓ Saved</p>}
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={handleJiraTest}
                                disabled={jiraTesting}
                                className="px-3 py-1.5 text-xs font-medium rounded-md border border-border-default bg-white text-text-primary hover:bg-surface-hover disabled:opacity-50"
                              >
                                {jiraTesting ? 'Testing...' : 'Test Connection'}
                              </button>
                              <button
                                type="button"
                                onClick={handleJiraSave}
                                disabled={jiraSaving}
                                className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {jiraSaving ? 'Saving...' : 'Save Jira Settings'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
